import { Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';
import { ReplaySubject, Subscription } from 'rxjs';
import { FormBuilder } from '@angular/forms';
import { UntilDestroy } from 'ngx-unificator/decorator';
import { debounceTime, distinctUntilChanged, filter, share, switchMap, tap } from 'rxjs/operators';
import { GamesService } from './games.service';
import { DOCUMENT } from '@angular/common';
import { GameFilterService } from './game-filter.service';
import { GameAttribute } from './game-attribute';
import { ScrollService } from '../scroll.service';
import { PlatformService } from '../platform.service';

@UntilDestroy({ checkProperties: true })
@Injectable({
  providedIn: 'root',
})
export class GameSearchService {
  private _fb = inject(FormBuilder);
  private _games = inject(GamesService);
  private _rendererFactory = inject(RendererFactory2);
  private _gamesFilters = inject(GameFilterService);
  private _scroll = inject(ScrollService);
  private _platform = inject(PlatformService);
  private _document = inject<Document>(DOCUMENT);

  /**
   * Subscription for search popup
   */
  public sub: Subscription;

  /**
   * Show or hide filters bar
   */
  public showFiltersBar: boolean;

  /**
   * Game limit for every games request
   */
  private readonly GAME_LIMIT = 18;

  /**
   * Founded games when search
   */
  public foundedGames$: ReplaySubject<any> = new ReplaySubject<any>(1);

  /**
   * Popular game to show them in popup
   */
  public popularGames$: ReplaySubject<any> = this._games.list({
    attribute: GameAttribute.HOT,
    limit: 4,
  });

  /**
   * Show/hide popup flag
   */
  public showPopup = false;

  /**
   * Overlay container for search popup
   * @private
   */
  private _overlayContainer: any;

  /**
   * Search form
   */
  public searchForm = this._fb.group({
    search: [null],
  });

  /**
   * Access to renderer var
   * @private
   */
  private _renderer: Renderer2;

  constructor() {
    this._renderer = this._rendererFactory.createRenderer(null, null);
  }

  /**
   * Search str
   */
  get searchStr(): string {
    return this.searchForm.get('search').value;
  }

  /**
   * Toggle search popup
   */
  public togglePopup() {
    this.showPopup = !this.showPopup;
    if (this.showPopup) {
      this._onAdd();
    } else {
      this.onClose();
    }
  }

  /**
   * Handle search input
   */
  public handleInput() {
    this.sub = this.searchForm
      .get('search')
      .valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => this.foundedGames$.next(null)),
        filter(() => !!this.searchStr),
        switchMap(res => {
          const params = this._resolveFilterParamsForSearch(this.searchStr);
          return this._games.list(params);
        }),
        share(),
      )
      .subscribe(games => {
        this.foundedGames$.next(games);
        if (this.showPopup) {
          return;
        }
      });
  }

  /**
   * On focus search method
   */
  public onFocus() {
    if (this.showPopup) {
      this.showFiltersBar = false;
      return;
    }
  }

  /**
   * On close popup
   * @private
   */
  public onClose() {
    setTimeout(() => {
      this._scroll.UnblockScroll();
      this._removeOverlay();
      this.showFiltersBar = false;
    }, 100);
  }

  /**
   * On add popup
   * @private
   */
  private _onAdd() {
    this._addOverlay();
    this._scroll.blockScroll();
    if (this.showFiltersBar) {
      this._onFocusInput();
    }
  }

  /**
   * Toggle filters bar
   */
  public toggleFiltersBar() {
    if (!this.showPopup) {
      this.togglePopup();
      this.showFiltersBar = true;
    } else {
      this.showFiltersBar = !this.showFiltersBar;
    }
  }

  /**
   * On focus input
   * @private
   */
  private _onFocusInput() {
    if (this._platform.isBrowser) {
      setTimeout(() => {
        const input: any = this._document.documentElement.querySelector('[formcontrolname="' + 'search' + '"]');
        input.focus();
      }, 100);
    }
  }

  /**
   * Add current filters params to search
   * @param searchValue
   */
  private _resolveFilterParamsForSearch(searchValue) {
    const params = {
      search: searchValue,
      limit: this.GAME_LIMIT,
    };

    if (this._gamesFilters.filter?.providers?.length) {
      (params as any)['provider[]'] = this._gamesFilters.filter?.providers;
    }
    if (this._gamesFilters.filter?.categories?.length) {
      (params as any)['category_in[]'] = this._gamesFilters.filter?.categories;
    }
    if (this._gamesFilters.filter?.attributes?.length) {
      (params as any)['attribute[]'] = this._gamesFilters.filter?.attributes;
    }

    return params;
  }

  /**
   * Add search popup overlay
   * @private
   */
  private _addOverlay() {
    this._overlayContainer = this._renderer.createElement('div');
    this._renderer.setStyle(this._document.body, 'position', 'relative');
    this._renderer.addClass(this._overlayContainer, 'search-overlay');
    this._renderer.appendChild(this._document.body, this._overlayContainer);
  }

  /**
   * Add search popup overlay
   * @private
   */
  private _removeOverlay() {
    const overlayClass = this._document.querySelector('.search-overlay');
    if (overlayClass) {
      this._renderer.removeStyle(this._document.body, 'position');
      this._renderer.removeClass(overlayClass, 'search-overlay');
      this._renderer.removeChild(this._overlayContainer, this._overlayContainer);
    }
  }
}
